import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { EmailMetadata } from '../../models/EmailMetadata'
import { hasTenMinutesPassed } from '../utils'
import { selectEmailResponse, selectEmailResponseLoading } from './emailResponse'
import { selectTasks, selectTasksLoading } from '../task/task'

interface EmailMetadataState {
  isLoading: boolean
  pollingEnabled: boolean
  emailMetadata: EntityState<EmailMetadata, string>
}

const emailMetadataAdapter = createEntityAdapter<EmailMetadata, string>({ selectId: (emailMetadata: EmailMetadata) => emailMetadata.externalId })

const initialState: EmailMetadataState = {
  isLoading: true,
  pollingEnabled: false,
  emailMetadata: emailMetadataAdapter.getInitialState(),
}

export const emailMetadataSlice = createSlice({
  name: 'emailMetadata',
  initialState,
  reducers: {
    setEmailMetadataLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setEmailMetadata: (state, action: PayloadAction<EmailMetadata[]>) => {
      emailMetadataAdapter.setMany(state.emailMetadata, action.payload)
    },
    updateEmailMetadata: (state, action: PayloadAction<EmailMetadata>) => {
      emailMetadataAdapter.setOne(state.emailMetadata, action.payload)
    },
    startEmailMetadataPolling: (state) => {
      state.pollingEnabled = true
    },
    stopEmailMetadataPolling: (state) => {
      state.pollingEnabled = false
    },
    resetEmailMetadata: () => initialState,
  },
})

export const { setEmailMetadataLoading, startEmailMetadataPolling, stopEmailMetadataPolling, setEmailMetadata, updateEmailMetadata, resetEmailMetadata } =
  emailMetadataSlice.actions

const emailMetadataSelectors = emailMetadataAdapter.getSelectors((state: RootState) => state.emailHub.emailMetadata.emailMetadata)

export const selectEmailMetadata = (state: RootState) => emailMetadataSelectors.selectAll(state)

export const selectEmailMetadataLoading = (state: RootState) => state.emailHub.emailMetadata.isLoading

export const selectEmailMetadataPollingEnabled = (state: RootState) => state.emailHub.emailMetadata.pollingEnabled

export const selectEmailMetadataForEmail = (state: RootState, emailId: string) => emailMetadataSelectors.selectById(state, emailId)

export const selectShouldPollMetadata = createSelector(
  [
    selectEmailMetadataLoading,
    selectEmailResponseLoading,
    (state: RootState, emailId: string) => emailMetadataSelectors.selectById(state, emailId),
    selectEmailResponse,
  ],
  (emailMetadataLoading, emailResponseLoading, emailMetadata, emailResponse) => {
    if (emailMetadataLoading || emailResponseLoading || !emailMetadata || emailResponse) {
      return false
    }

    const { typeProcessing, responseProcessing } = emailMetadata
    if (responseProcessing.finishedAt) {
      return false
    }
    if (responseProcessing.startedAt) {
      return !hasTenMinutesPassed(responseProcessing.startedAt)
    }
    if (typeProcessing.finishedAt) {
      return false
    }
    if (typeProcessing.startedAt) {
      return !hasTenMinutesPassed(typeProcessing.startedAt)
    }
    return false
  },
)

export const selectShouldPollEmailResponseTasks = createSelector(
  [selectTasksLoading, selectTasks, selectEmailMetadataLoading, (state: RootState, emailId: string) => emailMetadataSelectors.selectById(state, emailId)],
  (tasksLoading, tasks, emailMetadataLoading, emailMetadata) => {
    if (tasksLoading || emailMetadataLoading || !emailMetadata || tasks.length > 0) {
      return false
    }
    const { taskProcessing } = emailMetadata
    if (taskProcessing.finishedAt) {
      return false
    }
    if (taskProcessing.startedAt) {
      return !hasTenMinutesPassed(taskProcessing.startedAt)
    }
    return false
  },
)
